import { Button, Stack } from '@chakra-ui/react';
import { FormType } from '@pelicargo/types';
import { Show } from '@pelicargo/ui';
import { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';

import { useAuth } from '../../hooks/auth/useAuth';
import { SimpleCheckbox } from '../SimpleCheckbox';
import { SimpleForm } from '../SimpleForm';
import { SimpleInput } from '../SimpleInput';

export type ContactFormValues = {
  full_name: string;
  preferred_name: string;
  email: string;
  title: string;
  phone_number: string;
  should_send_account_invite: boolean;
};

type Props = {
  defaultValues: Partial<ContactFormValues>;
  type: FormType;
  onSubmit: (values: ContactFormValues) => Promise<void>;
};

export const ContactForm = ({ defaultValues, type, onSubmit }: Props) => {
  const methods = useForm<ContactFormValues>({ defaultValues });
  const [isLoading, setIsLoading] = useState(false);

  const { user } = useAuth();
  const domain = user?.email?.split('@')?.[1] || 'example.com';

  const submitValues = async (values: ContactFormValues) => {
    try {
      setIsLoading(true);

      await onSubmit({
        ...values,
        // ensure we only send the `should_send_account_invite` if the email has a whitelisted domain since the user can't see it in the UI without it
        // and we want it defaulted to true if they are able to see it in the UI
        should_send_account_invite: emailHasWhitelistedDomain && values.should_send_account_invite,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = methods.handleSubmit(submitValues);

  const email = methods.getValues('email');
  const emailHasWhitelistedDomain = useMemo(() => domain === email?.split('@')?.[1], [email]);

  return (
    <SimpleForm {...(methods as any)} onSubmit={handleSubmit}>
      <Stack direction="column" spacing={4} w="full">
        <Stack direction={{ base: 'column', md: 'row' }} w="full">
          <SimpleInput name="full_name" label="Full Name" placeholder="Jane Doe" isRequired />
          <SimpleInput name="preferred_name" label="Preferred Name" placeholder="Jane" isRequired />
        </Stack>
        <SimpleInput name="title" label="Title" placeholder="Booking Specialist" />
        <SimpleInput name="email" label="Email" placeholder={`someone@${domain}`} isRequired />

        <SimpleInput name="phone_number" label="Phone" placeholder="Enter phone number" />
      </Stack>
      <Show if={emailHasWhitelistedDomain}>
        <SimpleCheckbox
          name="should_send_account_invite"
          label="Invite to Organization"
          w="fit-content"
          size="md"
          variant="unstyled"
        />
      </Show>

      <Button colorScheme="primary" mt={4} w="full" isLoading={isLoading} onClick={handleSubmit}>
        {type} contact
      </Button>
    </SimpleForm>
  );
};
